import { TradingPanel } from '@trading-ui/core';
import { TradingChartLoader } from '@trading-ui/custom';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import MultiSeriesChart from '@/components/multiseries-chart/MultiSeriesChart';
import TableLoader from '@/components/table-loader/TableLoader';
import { formatDateForChart } from '@/utils/dates.utils';

import type { ConsumptionChartResponse } from '@/interfaces/consumer.interfaces';

import { useResizeObserver } from 'usehooks-ts';
import * as styles from './ChartPanel.css';

type Props = {
  chartData: ConsumptionChartResponse | undefined;
  isFetching: boolean;
};

export default function ChartPanel({ chartData, isFetching }: Props) {
  const { t } = useTranslation();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  // @ts-ignore
  const { width = 0 } = useResizeObserver({ ref: chartContainerRef });

  const processedChartData = useMemo(() => {
    if (!chartData || !chartData.consumptions.length) return undefined;

    return {
      ...chartData,
      consumptions: chartData.consumptions.map((series) => ({
        ...series,
        data: series.data.map((point) => ({
          ...point,
          x: formatDateForChart(point.x),
        })),
      })),
      averages: {
        nomination: chartData.averages?.nomination ?? 0,
        enagas: chartData.averages?.enagas ?? 0,
      },
    };
  }, [chartData]);

  return (
    <motion.div
      className={styles.root}
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}>
      <TradingPanel className={styles.panel}>
        <div className={styles.chartContainer} ref={chartContainerRef}>
          {!processedChartData ? (
            <TradingChartLoader style={{ height: '100%', width: '100%' }} />
          ) : (
            <div style={{ position: 'relative', height: '100%', width: '100%' }}>
              {isFetching && <TableLoader />}
              {width > 0 && processedChartData.consumptions && (
                <MultiSeriesChart
                  series={processedChartData.consumptions}
                  averages={processedChartData.averages}
                  yLabel={t('consumer.chartPanel.yLabel')}
                  // Subtracting to avoid horizontal overflow
                  width={width - 10}
                />
              )}
            </div>
          )}
        </div>
      </TradingPanel>
    </motion.div>
  );
}
